// src/components/atomic-crm/tasks/AddTask.tsx

import { Plus } from "lucide-react";
import {
  CreateBase,
  Form,
  required,
  useDataProvider,
  useGetIdentity,
  useNotify,
  useRecordContext,
  useResourceContext,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useState, useRef } from "react";
import { TextInput } from "@/components/ds/admin/text-input";
// import { DateInput } from "@/components/ds/admin/date-input";
import { DateTimeInput } from "@/components/admin/date-time-input";
import { SelectInput } from "@/components/ds/admin/select-input";
import { SaveButton } from "@/components/ds/admin/form";
import { Button } from "@/components/ds/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ds/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ds/ui/tooltip";
import { translateChoice } from "@/i18n/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { EntityTypePillSelector } from "./EntityTypePillSelector";
import { EntityAutocomplete } from "./EntityAutocomplete";
import { transformTaskEntityData } from "./taskEntityUtils";
import { MultiAssigneeInput } from "./MultiAssigneeInput";
import { syncActivityAssignees } from "../providers/supabase/syncActivityAssignees";

export const AddTask = ({
  selectContact,
  display = "chip",
  resource,
  noteId, // 👈 new
  noteType, // 👈 new — "contact" | "company" | "deal"
  parentId, // 👈 new — id of the parent (contact/company/deal)
}: {
  selectContact?: boolean;
  display?: "chip" | "icon";
  resource?: "contacts" | "companies" | "deals";
  noteId?: number;
  noteType?: "contact" | "company" | "deal";
  parentId?: number;
}) => {
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider();
  const [update] = useUpdate();
  const notify = useNotify();
  const { taskTypes, taskPriorities, taskStatuses } = useConfigurationContext();
  const record = useRecordContext();
  const resourceContext = useResourceContext({ resource });
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const pendingAssignees = useRef<number[]>([]); // 👈 add this
  const pendingContactId = useRef<number | null>(null); // 👈 add this

  const handleOpen = () => {
    setOpen(true);
    setOpen(true);
  };

  const handleSuccess = async (record: any) => {
    setOpen(false);

    // Sync assignees from the ref captured during transform
    if (record?.id) {
      try {
        await syncActivityAssignees(record.id, pendingAssignees.current);
      } catch (err) {
        console.error("[AddTask] assignee sync failed", err);
      }
      pendingAssignees.current = [];
    }

    // Bump the linked contact's last_seen if applicable
    const entity = transformTaskEntityData(record);
    const contactId =
      entity.contact_id ?? (noteType === "contact" ? parentId : null);
    if (contactId) {
      const contact = await dataProvider.getOne("contacts", { id: contactId });
      if (contact.data) {
        await update("contacts", {
          id: contact.data.id,
          data: { last_seen: new Date().toISOString() },
          previousData: contact.data,
        });
      }
    }

    notify(translate("crm.task.notification.created"));
  };

  if (!identity) return null;

  const translatedTaskTypes = taskTypes.map((type) => ({
    id: type,
    name: translateChoice(translate, "crm.task.type", type, type),
  }));

  const translatedTaskPriorities = taskPriorities.map((priority) => ({
    ...priority,
    name: translateChoice(
      translate,
      "crm.task.priority",
      priority.id,
      priority.name,
    ),
  }));

  const translatedTaskStatuses = taskStatuses.map((status) => ({
    ...status,
    name: translateChoice(translate, "crm.task.status", status.id, status.name),
  }));

  // Determine initial entity type and ID based on context
  const getInitialEntityData = () => {
    if (!record) return { entity_type: "contact" };

    if (resourceContext === "contacts") {
      return { entity_type: "contact", contact_id: record.id };
    }

    if (resourceContext === "companies") {
      return { entity_type: "company", company_id: record.id };
    }

    if (resourceContext === "deals") {
      return { entity_type: "deal", deal_id: record.id };
    }

    const inferredRecord = record as Record<string, unknown>;

    if ("first_name" in inferredRecord || "last_name" in inferredRecord) {
      return { entity_type: "contact", contact_id: record.id };
    }

    if ("category" in inferredRecord || "stage" in inferredRecord) {
      return { entity_type: "deal", deal_id: record.id };
    }

    if ("name" in inferredRecord) {
      return { entity_type: "company", company_id: record.id };
    }

    return { entity_type: "none" };
  };

  const initialEntityData = getInitialEntityData();

  const recordLabel =
    record?.first_name ||
    record?.name ||
    translate("crm.task.dialog.create.this_record");

  return (
    <>
      {display === "icon" ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="p-2 cursor-pointer"
                onClick={handleOpen}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {translate("crm.task.action.create_task")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <div className="my-2">
          <Button
            variant="outline"
            className="h-6 cursor-pointer"
            onClick={handleOpen}
            size="sm"
          >
            <Plus className="w-4 h-4" />
            {translate("crm.task.action.add_task")}
          </Button>
        </div>
      )}

      <CreateBase
        resource="crm_activities"
        record={{
          type: taskTypes[0] || "Call",
          ...initialEntityData,
          due_date: new Date().toISOString().slice(0, 10),
          sales_id: identity.id,
        }}
        transform={(data) => {
          // 👇 capture assignees before the transform strips them
          pendingAssignees.current = Array.isArray(data.assignee_ids)
            ? data.assignee_ids.map(Number)
            : data.assignee_ids != null
              ? [Number(data.assignee_ids)]
              : [];

          const entity = transformTaskEntityData(data);

          // Prefer the entity chosen in the form; fall back to the note's parent
          // (when the task is added from inside a note card in ActivityStream).
          const contactId =
            entity.contact_id ?? (noteType === "contact" ? parentId : null);
          pendingContactId.current = contactId;
          const contact_id =
            entity.contact_id ??
            (noteType === "contact" ? (parentId ?? null) : null);
          const company_id =
            entity.company_id ??
            (noteType === "company" ? (parentId ?? null) : null);
          const deal_id =
            entity.deal_id ?? (noteType === "deal" ? (parentId ?? null) : null);

          return {
            type: "task",
            text: data.text,
            date: data.due_date || new Date().toISOString(),
            due_date: data.due_date ?? null,
            done_date: null,
            priority: data.priority ?? "medium",
            status: data.status ?? "todo",
           // assigned_to: data.assigned_to ?? identity.id ?? null,
            contact_id:
              entity.contact_id ?? (noteType === "contact" ? parentId : null),
            company_id:
              entity.company_id ?? (noteType === "company" ? parentId : null),
            deal_id: entity.deal_id ?? (noteType === "deal" ? parentId : null),
            parent_activity_id: noteId ?? null,
            sales_id: identity.id,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
        }}
        mutationOptions={{ onSuccess: handleSuccess }}
      >
        <Dialog open={open} onOpenChange={() => setOpen(false)}>
          <DialogContent className="lg:max-w-xl overflow-y-auto max-h-9/10 top-1/20 translate-y-0">
            <Form
              className="flex flex-col gap-4"
              defaultValues={{
                ...initialEntityData,
                priority: "medium",
                status: "todo",
              //  assigned_to: identity.id, // keep for legacy — will be overwritten by the trigger
                assignee_ids: [identity.id], // new: pre-select self
              }}
            >
              <DialogHeader>
                <DialogTitle>
                  {record && !selectContact
                    ? translate("crm.task.dialog.create.title_for", {
                        name: recordLabel,
                      })
                    : translate("crm.task.dialog.create.title")}
                </DialogTitle>
                <DialogDescription>
                  {translate("crm.task.dialog.create.description")}
                </DialogDescription>
              </DialogHeader>
              <TextInput
                autoFocus
                source="text"
                label={translate("crm.task.field.description")}
                validate={required()}
                multiline
                className="m-0"
                helperText={false}
              />

              {/* Entity Type Selector */}
              <div className="grid grid-cols-2 gap-4 mt-2">
                <EntityTypePillSelector />
              </div>

              {/* Entity Autocomplete */}
              <div className="mt-2">
                <EntityAutocomplete helperText={false} />
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-2 gap-4 mt-2">
                <DateTimeInput
                  source="due_date"
                  helperText={false}
                  validate={required()}
                />
                <SelectInput
                  source="type"
                  validate={required()}
                  choices={translatedTaskTypes}
                  helperText={false}
                  label={translate("crm.task.field.type")}
                />
                <SelectInput
                  source="priority"
                  choices={translatedTaskPriorities}
                  helperText={false}
                />
                <SelectInput
                  source="status"
                  choices={translatedTaskStatuses}
                  helperText={false}
                />
              </div>
              {/* After the priority/status grid — full width */}
              <div className="mt-2">
                <MultiAssigneeInput
                  source="assignee_ids"
                  helperText="People who need to act on this."
                />
              </div>
              <DialogFooter className="w-full justify-end">
                <SaveButton />
              </DialogFooter>
            </Form>
          </DialogContent>
        </Dialog>
      </CreateBase>
    </>
  );
};
